// ⚠️ Keep in sync with be/services/bookingConflictsWatchdogService.js
// Any change to Arabic labels, buckets, or plural rules must be mirrored there.
// Drift between the 07:00 Riyadh email digest and the /alerts UI page will confuse ops.

const UNKNOWN_PROPERTY_LABEL = '(عقار غير معروف)';

// Upstream creation paths pass various forms of the platform string.
// Lookup is case-insensitive; unknown strings render as-is.
const PROVIDER_LABEL = {
  'airbnb': 'Airbnb',
  'booking': 'Booking.com',
  'booking.com': 'Booking.com',
  'bookingcom': 'Booking.com',
  'vrbo': 'Vrbo',
  'expedia': 'Expedia',
  'direct': 'Direct'
};

export const BUCKET = {
  TODAY: 'today',
  THIS_WEEK: 'thisWeek',
  OLD: 'old'
};

export const BUCKET_TITLE = {
  [BUCKET.TODAY]: 'غير محلول اليوم',
  [BUCKET.THIS_WEEK]: 'غير محلول هذا الأسبوع',
  [BUCKET.OLD]: 'غير محلول منذ فترة طويلة'
};

// Blue → Amber → Red escalation mirrors the email digest.
export const BUCKET_COLOR = {
  [BUCKET.TODAY]: '#0066cc',
  [BUCKET.THIS_WEEK]: '#cc8800',
  [BUCKET.OLD]: '#cc0000'
};

// 1 → singular, 2 → dual, 3–10 → plural, 11+ → singular (Arabic nominal form).
export function arabicPlural(count, singular, dual, plural) {
  if (count === 1) return singular;
  if (count === 2) return dual;
  if (count >= 3 && count <= 10) return plural;
  return singular;
}

export function hoursSince(date) {
  if (!date) return Infinity;
  const then = new Date(date);
  if (isNaN(then.getTime())) return Infinity;
  return (Date.now() - then.getTime()) / (1000 * 60 * 60);
}

export function formatAgeLabel(hours) {
  if (!isFinite(hours) || hours < 0) return '—';
  if (hours < 1) return 'منذ أقل من ساعة';
  if (hours < 24) {
    const h = Math.floor(hours);
    return `منذ ${h} ${arabicPlural(h, 'ساعة', 'ساعتان', 'ساعات')}`;
  }
  const d = Math.floor(hours / 24);
  return `منذ ${d} ${arabicPlural(d, 'يوم', 'يومان', 'أيام')}`;
}

export function bucketForAge(ageHours) {
  if (ageHours < 24) return BUCKET.TODAY;
  if (ageHours < 24 * 7) return BUCKET.THIS_WEEK;
  return BUCKET.OLD;
}

export function formatPlatformLabel(platform) {
  if (!platform) return null;
  const key = String(platform).trim().toLowerCase();
  if (!key) return null;
  return PROVIDER_LABEL[key] || String(platform).trim();
}

export function formatPropertyLabel(property) {
  if (!property) return UNKNOWN_PROPERTY_LABEL;
  const code = property.propertyCode;
  const name = property.name || UNKNOWN_PROPERTY_LABEL;
  return code ? `[${code}] ${name}` : name;
}

// Enrich each alert with ageHours/ageLabel/bucket and split into 3 arrays.
// Groups by `createdAt` (NOT updatedAt) so resolved/reopened alerts don't jump buckets.
// Oldest-first within each bucket — matches the email digest's ordering.
export function groupByAge(alerts) {
  const buckets = {
    [BUCKET.TODAY]: [],
    [BUCKET.THIS_WEEK]: [],
    [BUCKET.OLD]: []
  };

  const enriched = (alerts || []).map(alert => {
    const ageHours = hoursSince(alert.createdAt);
    return {
      ...alert,
      ageHours,
      ageLabel: formatAgeLabel(ageHours),
      bucket: bucketForAge(ageHours)
    };
  });

  enriched.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  for (const alert of enriched) {
    buckets[alert.bucket].push(alert);
  }

  return buckets;
}
